/**
 * `<ScreenTabs>` — the strip above the screener: which saved screen it is showing.
 *
 *     [ Default ][ Breakouts ][ My Watch ]                    [+]
 *     [ 🔒 Momentum ALPHA ][ 🔒 Quality ALPHA ]        [ +4 ▾ ]
 *
 * **Two rows and a measured overflow, not a scroll region** — `GroupBar`'s
 * arrangement exactly, and every load-bearing detail here is borrowed from it
 * because each is a silent failure on its own:
 *
 * - **An off-screen ruler that always holds every tab.** Measuring the strip
 *   itself reads the DOM left by the previous render, which holds only the tabs
 *   that were shown — so from a cold start there is nothing to measure. The
 *   corollary is that a ruler tab and a real tab must render the same markup, or
 *   the widths are of something else.
 * - **A budget is the row's width plus the chevron's**, because the chevron is a
 *   sibling and has already narrowed `clientWidth`; reserving for it again in
 *   the arithmetic charges twice.
 * - **Zero width means "no layout yet", not "a strip of zero width".** jsdom
 *   reports every offset as 0, and so does a browser for one frame before first
 *   paint. Filling on those numbers hides all but the first tab, which is
 *   indistinguishable from a real overflow and then sticks.
 * - **The selected tab is never in the overflow.** Picking one from the chevron
 *   list and having it stay hidden is a selection with nothing on screen to
 *   show for it — and the controls that rename and delete it live on the tab.
 *
 * ## What the strip does not decide
 *
 * Nothing here is async and nothing here writes. The host owns creating,
 * renaming and deleting, because those are server calls with rollback, and a
 * component that owned them could not be rendered in a test without a server.
 * This draws what it is given and reports what was clicked.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { CHIP_GAP, fillRows, type ChipRows } from "../lib/chip-rows";
import { canDeleteScreen, isAuthored, isClub, isOwn, orderTabs, type Screen } from "../lib/screens";
import { Menu, MenuRow } from "./Menu";

export interface ScreenTabsProps {
  screens: readonly Screen[];
  /** The `screenRef` on the panel, or absent while it still has none. */
  selected?: string;
  onSelect: (key: string) => void;
  onAdd: () => void;
  onRename: (key: string, name: string) => void;
  onRemove: (key: string) => void;
  /**
   * Take what is on screen — the club's half and yours — into a screen of your
   * own. Offered on a club tab only: duplicating one of your own is a feature
   * nobody asked for, and the club's is the one you cannot edit outright.
   */
  onDuplicate?: (key: string) => void;
  /**
   * The group being authored (`/auth/me` `publishTo`), absent otherwise. Its own
   * screens lose the 🔒 (owner, 2026-10-06): while authoring they are edited as
   * a draft, so the lock would say the one thing that is no longer true.
   */
  publishTo?: string | null;
}

export function ScreenTabs({
  screens,
  selected,
  onSelect,
  onAdd,
  onRename,
  onRemove,
  onDuplicate,
  publishTo,
}: ScreenTabsProps) {
  const tabs = orderTabs(screens);
  /** A club's screen you cannot edit — every club screen but the one you author. */
  const locked = (screen: Screen) => isClub(screen) && !(publishTo && isAuthored(screen, publishTo));

  const rowARef = useRef<HTMLDivElement | null>(null);
  const rowBRef = useRef<HTMLDivElement | null>(null);
  const rulerRef = useRef<HTMLDivElement | null>(null);
  const overflowRef = useRef<HTMLButtonElement | null>(null);
  const [fit, setFit] = useState<ChipRows>({ row1: [], row2: [], hidden: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const measure = useCallback(() => {
    const rowA = rowARef.current;
    const rowB = rowBRef.current;
    const ruler = rulerRef.current;
    if (!rowA || !rowB || !ruler) return;

    const widths = Array.from(ruler.children).map((c) => (c as HTMLElement).offsetWidth);
    const chevron = overflowRef.current;
    const budgetA = rowA.clientWidth + (chevron ? chevron.offsetWidth + CHIP_GAP : 0);
    const budgetB = rowB.clientWidth;

    const all = Array.from({ length: widths.length }, (_, i) => i);
    const requiredIndex = tabs.findIndex((t) => t.key === selected);
    const next =
      budgetA > 0
        ? fillRows(widths, budgetA, budgetB, {
            required: requiredIndex >= 0 ? [requiredIndex] : [],
          })
        : { row1: all, row2: [], hidden: [] };
    setFit((prev) => (sameRows(prev, next) ? prev : next));
  }, [tabs, selected]);

  useLayoutEffect(measure);

  useEffect(() => {
    const rowA = rowARef.current;
    if (!rowA || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(rowA);
    return () => observer.disconnect();
  }, [measure]);

  const startRename = (screen: Screen) => {
    setEditing(screen.key);
    setDraft(screen.name);
  };

  const commitRename = () => {
    const name = draft.trim();
    const key = editing;
    setEditing(null);
    // An empty name is a cancel, not a rename to nothing: `uq_preset_owner_name`
    // would take the first one and refuse the second, and a tab with no label is
    // unclickable in a strip that is all labels.
    if (key && name && name !== screens.find((s) => s.key === key)?.name) {
      onRename(key, name);
    }
  };

  function tab(index: number) {
    const screen = tabs[index];
    if (!screen) return null;
    const active = screen.key === selected;
    const own = isOwn(screen);
    const club = isClub(screen);
    const lock = locked(screen);

    if (editing === screen.key) {
      return (
        <input
          key={screen.key}
          autoFocus
          aria-label="Screen name"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") setEditing(null);
          }}
          style={NAME_INPUT}
        />
      );
    }

    return (
      <div key={screen.key} style={tabStyle(active)}>
        <button
          type="button"
          role="tab"
          aria-selected={active}
          // **The name is the rename gesture on a tab you already own and are
          // on** — `LayoutPanel`'s rule for a layout row, and the pencil beside
          // it is the discoverable half, since a clickable name is not visible
          // until you try it.
          onClick={() => (active && own ? startRename(screen) : onSelect(screen.key))}
          title={
            lock
              ? `${screen.name} — ${screen.ownerName ?? screen.ownerKey}'s screen, so its columns and filters are theirs`
              : club
                ? `${screen.name} — you are authoring it; edits are a draft until you publish`
              : active && own
                ? "Rename"
                : `Show ${screen.name}`
          }
          style={NAME_BUTTON}
        >
          {lock && <span style={LOCK} aria-hidden>🔒</span>}
          <span>{screen.name}</span>
          {club && screen.ownerKey && <span style={CLUB}>{screen.ownerName ?? screen.ownerKey}</span>}
          {/* An unpublished draft (`docs/plans/author-screen-drafts.md`): only
              ever set on a screen the account is authoring, so members never
              see it. On every tab, not the active one alone — a draft left on
              another screen is the one an author forgets to publish. */}
          {screen.hasDraft && (
            <span style={DRAFT} role="img" aria-label="Unpublished draft" title="Unpublished draft">
              ✎
            </span>
          )}
        </button>

        {/* **The copy lives on the tab it copies.** It was an Apply button in a
            list of club screens, in a tab called Presets that also published —
            two acts under one word, and the list was a second place the same
            screens appeared now that each is a tab. Here the object is in front
            of you and the verb is beside it. */}
        {active && club && onDuplicate && (
          <button
            type="button"
            aria-label={`Duplicate ${screen.name}`}
            title="Copy this screen — their columns and yours — into one of your own"
            onClick={() => onDuplicate(screen.key)}
            style={ICON}
          >
            ⧉
          </button>
        )}

        {active && own && (
          <button
            type="button"
            aria-label={`Rename ${screen.name}`}
            title="Rename"
            onClick={() => startRename(screen)}
            style={ICON}
          >
            ✎
          </button>
        )}

        {/* **Absent, not disabled, on the last one you own.** There is no state
            in which it becomes available — a workspace with no screens has no
            tab to fall back to — so a greyed × would invite a hunt for the
            reason. A club's has none either: leaving the club is what removes
            it, and the server answers 404 to a delete aimed at one. */}
        {active && canDeleteScreen(screens, screen.key) && (
          <button
            type="button"
            aria-label={`Delete ${screen.name}`}
            title="Delete this screen"
            onClick={() => onRemove(screen.key)}
            style={ICON}
          >
            ×
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={BAR} role="tablist" aria-label="Screens">
      {/* The ruler: every tab, off-screen, never interactive. Same markup as a
          real tab, or the widths measured are of something else. */}
      <div ref={rulerRef} aria-hidden style={RULER}>
        {tabs.map((screen) => (
          <span key={`ruler-${screen.key}`} style={tabStyle(false)}>
            {locked(screen) && <span style={LOCK}>🔒</span>}
            <span style={NAME_BUTTON}>{screen.name}</span>
            {isClub(screen) && screen.ownerKey && <span style={CLUB}>{screen.ownerName ?? screen.ownerKey}</span>}
            {screen.hasDraft && <span style={DRAFT}>✎</span>}
          </span>
        ))}
      </div>

      <div style={ROW}>
        <div ref={rowARef} style={TABS}>
          {fit.row1.map(tab)}
        </div>
        {fit.hidden.length > 0 && (
          <button
            ref={overflowRef}
            type="button"
            style={BUTTON}
            title={`${fit.hidden.length} more`}
            onClick={() => setOpen((v) => !v)}
          >
            +{fit.hidden.length} ▾
          </button>
        )}
        {/* The word, not the glyph. A `+` beside a row of named tabs reads as
            "add a tab" only once you have worked out that the row is tabs — and
            it is the one control here that makes something rather than
            selecting it. */}
        <button type="button" style={BUTTON} title="New screen" aria-label="New screen"
          onClick={onAdd}>
          New
        </button>
        {open && (
          <Menu anchorRef={overflowRef} onClose={() => setOpen(false)}>
            {fit.hidden.map((i) => {
              const screen = tabs[i];
              return screen ? (
                <MenuRow
                  key={screen.key}
                  active={screen.key === selected}
                  onSelect={() => {
                    onSelect(screen.key);
                    setOpen(false);
                  }}
                >
                  {locked(screen) ? `🔒 ${screen.name}` : screen.name}
                </MenuRow>
              ) : null;
            })}
          </Menu>
        )}
      </div>

      {/* Always rendered, even empty: `fillRows` needs its width to know whether
          a second row is available, and a row that appears only once it is full
          can never become full. It collapses to nothing when it holds nothing. */}
      <div style={ROW}>
        <div ref={rowBRef} style={TABS}>
          {fit.row2.map(tab)}
        </div>
      </div>
    </div>
  );
}

function sameRows(a: ChipRows, b: ChipRows): boolean {
  const same = (x: readonly number[], y: readonly number[]) =>
    x.length === y.length && x.every((v, i) => v === y[i]);
  return same(a.row1, b.row1) && same(a.row2, b.row2) && same(a.hidden, b.hidden);
}

const BAR: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: "4px 6px",
  background: "var(--qf-surface-alt)",
  borderBottom: "1px solid var(--qf-border)",
  position: "relative",
};

const ROW: React.CSSProperties = { display: "flex", gap: CHIP_GAP, alignItems: "center" };

const TABS: React.CSSProperties = {
  display: "flex",
  gap: CHIP_GAP,
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
};

const RULER: React.CSSProperties = {
  position: "absolute",
  left: -99999,
  top: 0,
  display: "flex",
  gap: CHIP_GAP,
  pointerEvents: "none",
  visibility: "hidden",
};

function tabStyle(active: boolean): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    flex: "0 0 auto",
    padding: "2px 4px 2px 8px",
    borderRadius: 5,
    border: `1px solid ${active ? "var(--qf-border)" : "transparent"}`,
    background: active ? "var(--qf-background)" : "transparent",
    color: active ? "var(--qf-text)" : "var(--qf-text-secondary)",
    fontWeight: active ? 600 : 400,
    fontSize: 12,
    whiteSpace: "nowrap",
  };
}

const NAME_BUTTON: React.CSSProperties = {
  font: "inherit",
  color: "inherit",
  background: "none",
  border: 0,
  padding: 0,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
};

const NAME_INPUT: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  width: 120,
  padding: "2px 6px",
  borderRadius: 5,
  border: "1px solid var(--qf-accent)",
  background: "var(--qf-background)",
  color: "var(--qf-text)",
};

const ICON: React.CSSProperties = {
  font: "inherit",
  fontSize: 11,
  lineHeight: 1,
  color: "var(--qf-text-muted)",
  background: "none",
  border: 0,
  padding: "2px 3px",
  cursor: "pointer",
};

const LOCK: React.CSSProperties = { fontSize: 9, color: "var(--qf-text-muted)" };

const CLUB: React.CSSProperties = {
  fontSize: 9,
  letterSpacing: "0.04em",
  padding: "1px 4px",
  borderRadius: 3,
  border: "1px solid var(--qf-border)",
  color: "var(--qf-text-muted)",
};

/** The draft mark: small, and the accent, so it reads as "pending" not "locked". */
const DRAFT: React.CSSProperties = {
  fontSize: 10,
  color: "var(--qf-accent)",
};

const BUTTON: React.CSSProperties = {
  font: "inherit",
  fontSize: 11,
  flex: "0 0 auto",
  padding: "3px 8px",
  borderRadius: 5,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-background)",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
};
